// src/layouts/AdminLayout.tsx
import { useState, useEffect, useRef } from "react";
import SharedBreadcrumb from "../components/Breadcrumb";
import NotificationBell from "../components/NotificationBell";
import {
  Link,
  Navigate,
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAdminUser,
  selectAdminHydrated,
  selectIsSuperAdmin,
  selectAdminRole,
  clearAdminCredentials,
} from "../app/features/adminAuthSlice";
import { useAdminLogoutMutation } from "../app/api/adminAuthApi";

import { BookOpen, ChartLine, ClipboardCheck, CreditCard, GraduationCap, LayoutDashboard, MapPin, MessagesSquare, Package, PenLine, Presentation, Settings, ShieldCheck, Star, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../components/Ico";
/* ─────────────────────────────────────────────────────────────────
   NAV ITEMS
───────────────────────────────────────────────────────────────── */

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  superAdminOnly?: boolean;
  /** Tutors only see items marked for them. */
  tutor?: boolean;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", to: "/admin/dashboard", icon: LayoutDashboard },
  { label: "Enrolments", to: "/admin/enrolments", icon: ClipboardCheck },
  { label: "Students", to: "/admin/students", icon: GraduationCap },
  { label: "Reviews", to: "/admin/reviews", icon: PenLine, tutor: true },
  { label: "Showcase panel", to: "/admin/reviews/showcases", icon: Star, tutor: true },
  { label: "Progress", to: "/admin/progress", icon: ChartLine },
  { label: "Messages", to: "/admin/messages", icon: MessagesSquare },
  { label: "Parents", to: "/admin/parents", icon: Users },
  { label: "Lesson library", to: "/admin/curriculum", icon: BookOpen },
  { label: "Clusters", to: "/admin/clusters", icon: MapPin },
  { label: "Kits", to: "/admin/kits", icon: Package },
  { label: "Showcases", to: "/admin/showcases", icon: Presentation },
  { label: "Payments", to: "/admin/payments", icon: CreditCard },
  { label: "Team", to: "/admin/team", icon: ShieldCheck, superAdminOnly: true },
  { label: "Settings", to: "/admin/settings", icon: Settings },
];

/* ─────────────────────────────────────────────────────────────────
   BREADCRUMB
───────────────────────────────────────────────────────────────── */

const routeLabels: Record<string, string> = {
  reviews: "Reviews",
  clusters: "Clusters",
  kits: "Kits",
  messages: "Messages",
  dashboard: "Dashboard",
  admin: "Admin",
  enrolments: "Enrolments",
  students: "Students",
  progress: "Progress",
  payments: "Payments",
  parents: "Parents",
  curriculum: "Curriculum",
  showcases: "Showcases",
  team: "Team",
  settings: "Settings",
};

function Breadcrumb() {
  return <SharedBreadcrumb labels={routeLabels} root="admin" />;
}

/* ─────────────────────────────────────────────────────────────────
   USER MENU
───────────────────────────────────────────────────────────────── */

function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectAdminUser);
  const [adminLogout] = useAdminLogoutMutation();

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => {
    try {
      await adminLogout().unwrap();
    } catch {
      // continue regardless
    }
    dispatch(clearAdminCredentials());
    navigate("/login");
  };

  const roleBadgeColor: Record<string, string> = {
    super_admin: "bg-[#1b2d5e] text-white",
    admin: "bg-[#efb0a4] text-[#5b2e28]",
    staff: "bg-[#e5e8ef] text-[#4a44b0]",
    tutor: "bg-[#edfaf4] text-[#2a7a56]",
  };

  const roleLabel: Record<string, string> = {
    super_admin: "Super Admin",
    admin: "Admin",
    staff: "Staff",
    tutor: "Tutor",
  };

  const initials =
    `${user?.firstName?.charAt(0) ?? ""}${user?.lastName?.charAt(0) ?? ""}`.toUpperCase() || "?";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 rounded-2xl border border-[#dde2ec] bg-white px-3 py-2 text-sm text-[#16213e] transition hover:border-[#c9cfe0] hover:bg-[#f9fafc]"
      >
        {/* Avatar */}
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl bg-[#1b2d5e] text-xs font-bold text-white">
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={initials}
              className="h-7 w-7 rounded-xl object-cover"
            />
          ) : (
            initials
          )}
        </span>
        <span className="hidden max-w-[120px] truncate font-medium sm:block">
          {user?.firstName}
        </span>
        <svg
          className={`h-3.5 w-3.5 text-[#8189a0] transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-2xl border border-[#dde2ec] bg-white shadow-[0_16px_48px_rgba(27,45,94,0.14)]">
          {/* User info */}
          <div className="border-b border-[#ebeff7] bg-[#f9fafc] px-4 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1b2d5e] text-sm font-bold text-white">
                {initials}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#16213e]">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-[#8189a0]">{user?.email}</p>
              </div>
            </div>
            {user?.role && (
              <span
                className={`mt-3 inline-flex rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold uppercase tracking-wider ${roleBadgeColor[user.role] ?? "bg-[#e5e8ef] text-[#4a44b0]"}`}
              >
                {roleLabel[user.role] ?? user.role}
              </span>
            )}
          </div>

          {/* Links */}
          <div className="p-1.5">
            {[
              { label: "My Profile", to: "/admin/settings#profile" },
              { label: "Change Password", to: "/admin/settings#password" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#3b4560] transition hover:bg-[#f1f3f8]"
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-[#ebeff7] p-1.5">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-[#c0392b] transition hover:bg-[#fff5f5]"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SIDEBAR NAV ITEM
───────────────────────────────────────────────────────────────── */

function SideNavItem({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  onClick?: () => void;
}) {
  const { pathname } = useLocation();
  // The showcase panel sits under /admin/reviews but has its own nav item
  const shadowed =
    item.to === "/admin/reviews" && pathname.startsWith("/admin/reviews/showcases");
  return (
    <NavLink
      to={item.to}
      end={item.to === "/admin"}
      onClick={onClick}
      className={({ isActive: active }) => {
        const isActive = active && !shadowed;
        return [
          "group flex items-center gap-3 rounded-[14px] px-3 py-2.5 text-sm transition-all",
          isActive
            ? "bg-[#1b2d5e] font-semibold text-white shadow-[0_4px_16px_rgba(27,45,94,0.32)]"
            : "text-[#5a6278] hover:bg-[#ebeff7] hover:text-[#16213e]",
        ].join(" ");
      }}
      title={collapsed ? item.label : undefined}
    >
      {({ isActive: active }) => {
        const isActive = active && !shadowed;
        return (
        <>
          <span
            className={`flex-shrink-0 text-base leading-none transition-transform group-hover:scale-110 ${isActive ? "text-white" : "text-[#1b2d5e]"}`}
          >
            <Ico icon={item.icon} />
          </span>
          {!collapsed && (
            <span className="flex-1 truncate leading-none">{item.label}</span>
          )}
          {!collapsed && item.badge && (
            <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#efb0a4] px-1.5 text-[11.5px] font-bold text-[#5b2e28]">
              {item.badge}
            </span>
          )}
        </>
        );
      }}
    </NavLink>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SIDEBAR
───────────────────────────────────────────────────────────────── */

interface SidebarProps {
  collapsed: boolean;
  onClose?: () => void;
}

function Sidebar({ collapsed, onClose }: SidebarProps) {
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const isTutor = useSelector(selectAdminRole) === "tutor";
  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      (isTutor ? item.tutor : true) && (!item.superAdminOnly || isSuperAdmin),
  );

  return (
    <aside
      className={[
        "flex h-full flex-col border-r border-[#dde2ec] bg-white transition-all duration-300",
        collapsed ? "w-[68px]" : "w-[232px]",
      ].join(" ")}
    >
      {/* Logo */}
      <div
        className={[
          "flex h-16 flex-shrink-0 items-center border-b border-[#ebeff7]",
          collapsed ? "justify-center px-3" : "gap-2.5 px-5",
        ].join(" ")}
      >
        {/* FSL icon mark */}
        <img src="/images/logo.png" alt="" className="h-8 w-8 flex-shrink-0 rounded-xl" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="truncate text-[13px] font-bold leading-tight tracking-[-0.03em] text-[#16213e]">
              Future Smart Lab
            </p>
            <p className="text-[11.5px] font-medium uppercase tracking-widest text-[#8189a0]">
              Admin Portal
            </p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-4">
        <div className="space-y-0.5">
          {visibleItems.map((item) => (
            <SideNavItem
              key={item.to}
              item={item}
              collapsed={collapsed}
              onClick={onClose}
            />
          ))}
        </div>
      </nav>

      {/* Bottom version tag */}
      {!collapsed && (
        <div className="border-t border-[#ebeff7] px-5 py-4">
          <p className="text-[11.5px] font-medium uppercase tracking-widest text-[#c9cfe0]">
            FSL Admin v1.0
          </p>
        </div>
      )}
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────
   MOBILE DRAWER
───────────────────────────────────────────────────────────────── */

function MobileDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />
      {/* Drawer */}
      <div className="fixed inset-y-0 left-0 z-50 flex lg:hidden">
        <Sidebar collapsed={false} onClose={onClose} />
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TOPBAR
───────────────────────────────────────────────────────────────── */

interface TopbarProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileDrawer: () => void;
}

function Topbar({
  sidebarCollapsed,
  onToggleSidebar,
  onOpenMobileDrawer,
}: TopbarProps) {
  return (
    <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-[#dde2ec] bg-white px-4 sm:px-6">
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={onOpenMobileDrawer}
          className="rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8] lg:hidden"
          aria-label="Open menu"
        >
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 4h12M2 8h12M2 12h12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Desktop collapse toggle */}
        <button
          onClick={onToggleSidebar}
          className="hidden rounded-xl border border-[#dde2ec] p-2 text-[#5a6278] transition hover:bg-[#f1f3f8] lg:flex"
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            className={`h-4 w-4 transition-transform ${sidebarCollapsed ? "rotate-180" : ""}`}
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M10 4L6 8l4 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <Breadcrumb />
      </div>

      <div className="flex items-center gap-2.5">
        <NotificationBell />

        <UserMenu />
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ADMIN LAYOUT — root
───────────────────────────────────────────────────────────────── */

export default function AdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const location = useLocation();
  const { pathname, hash } = location;
  const user = useSelector(selectAdminUser);
  const hydrated = useSelector(selectAdminHydrated);
  const role = user?.role;
  const mainRef = useRef<HTMLElement>(null);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  // The page area scrolls on its own, so a new page starts at the top
  // (links to a section, like #password, scroll there instead)
  useEffect(() => {
    if (!hash) mainRef.current?.scrollTo(0, 0);
  }, [pathname, hash]);

  // No staff session (signed out, expired, or a parent/student account) →
  // sign in, then come back here
  if (!hydrated) return null;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Tutors work in the review queue only
  if (role === "tutor" && !pathname.startsWith("/admin/reviews")) {
    return <Navigate to="/admin/reviews" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#f1f3f8] font-sans antialiased">
      {/* Desktop sidebar */}
      <div className="hidden flex-shrink-0 lg:flex">
        <Sidebar collapsed={sidebarCollapsed} />
      </div>

      {/* Mobile drawer */}
      <MobileDrawer
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      />

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((v) => !v)}
          onOpenMobileDrawer={() => setMobileDrawerOpen(true)}
        />

        {/* Page content */}
        <main ref={mainRef} className="flex-1 overflow-y-auto">
          {/* Inner wrapper — gives pages a consistent max-width and padding */}
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
